const PGAdapter = require('./../common/pg-adapter')
const { getInsertProperties, getListArgs } = require('../tools/sql-tools')
const v4 = require('uuid/v4')
const COLUMN_TYPES = {
  id: 'uuid',
  buyprice: 'numeric',
  sellprice: 'numeric',
  markup: 'numeric',
  discount: 'numeric',
  month: 'timestamptz',
  date: 'date',
  created_at: 'timestamptz'
}

class PricePGAdapter extends PGAdapter {
  constructor ({pgConnection, username, logger}) {
    const columns = [
      'id',
      'sku',
      'date',
      'buyprice',
      'sellprice',
      'discount',
      'markup',
      'service_id'
    ]
    const virtualColumns = [
      'product',
      'market',
      'month'
    ]
    const opts = {
      addUuid: true,
      strictColumns: false,
      virtualColumns
    }
    super(pgConnection, 'real.price_history', username, columns, 'id', {}, logger, opts)
  }
  async getList ({ ordering = this.idColumn, filter = {}, limit = 50, offset = 0 } = {}) {
    const tableExpression = `
      (
        select
          ph.id,
          p.product_id as sku,
          p.product,
          p.market,
          p.supplier,
          ph.date,
          ph.month,
          ph.buyprice,
          ph.sellprice,
          ph.discount
          from
          real.price_history ph inner join avocado.vw_products p on p.product_id = ph.sku
      ) t
    `
    return PGAdapter.prototype.getList.call(this, {ordering, filter, limit, offset, tableExpression})
  }

  async create (data) {
    if (!data || (typeof data !== 'object')) {
      throw new Error('Object entry required')
    }

    const entries = Array.isArray(data) ? data : [data]
    const createdAt = new Date().toJSON()
    const parsedRows = entries.map(r => ({
      id: v4(),
      sku: r.sku,
      buyprice: r.buyprice,
      sellprice: r.sellprice,
      markup: r.markup,
      date: r.date,
      discount: r.discount,
      created_at: createdAt
    }))

    const { columns, values, placeholders } = getInsertProperties(parsedRows, null, 0, COLUMN_TYPES)
    const distinctSkus = [...new Set(parsedRows.map(r => r.sku))]
    const { args } = getListArgs(distinctSkus, values.length + 1)
    const { rows: [row] } = await this.pgConnection.query(`
      with skus_service_id as (
        select
          s.old_sku_id as sku_id,
          ds.id as service_id
        from avocado.data_productsku s
        left join avocado.data_market m on s.market_id = m.id
        left join avocado.data_service ds on m.fs_service_id = ds.fsid
        where s.old_sku_id in (${args})
      ),
      insert_rows as (
        select
          *
        from (
          values ${placeholders}
        ) v (id, sku, buyprice, sellprice, markup, date, discount, created_at)
      )
      insert into real.price_history (${columns}, service_id)
      select 
        i.id,
        i.sku,
        i.buyprice,
        i.sellprice,
        i.markup,
        i.date,
        i.discount,
        i.created_at,
        sk.service_id
      from insert_rows i
      left join skus_service_id sk on sk.sku_id = i.sku
      returning *
    `, [...values, ...distinctSkus])

    if (row) {
      await this.clearCaches(row)
    }
    return row
  }

  async upsert (data) {
    const res = await PGAdapter.prototype.upsert.call(this, data, {conflictIdColumns: ['sku', 'date']})
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async update (data) {
    const res = await PGAdapter.prototype.update.call(this, data)
    if (res) {
      await this.clearCaches(data)
    }
    return res
  }

  async delete (id) {
    const obj = await this.getOne(id)
    if (obj) {
      const res = await PGAdapter.prototype.delete.call(this, id)
      if (res) {
        await this.clearCaches(obj)
      }
      return res
    }
  }

  async clearCaches ({date, sku}) {
    if (date && sku) {
      this.logger.debug('clearing cached values')
      await this.pgConnection.query(`DELETE FROM real.buyprice_daily WHERE date >= $1 and sku = $2;`, [date, sku])
      await this.pgConnection.query(`DELETE FROM real.sellprice_daily WHERE date >= $1 and sku = $2;`, [date, sku])
      await this.pgConnection.query(`
        UPDATE real.presentation_cost_basis_base
        SET _deleted=true
        WHERE date >= $1
        AND presentation_id = (
          SELECT presentation_id
          FROM avocado.data_productsku
          WHERE old_sku_id = $2
        )
      `, [date, sku])
      await this.pgConnection.query(`
        DELETE
        FROM real.presentation_cost_basis
        WHERE date >= $1
        AND presentation_id = (
          SELECT presentation_id
          FROM avocado.data_productsku
          WHERE old_sku_id = $2
        )
      `, [date, sku])
    }
  }

  /**
   * @param {Object} params
   * @param {string[]} params.skus
   * @param {string} params.locationFsid
   * @param {string} params.serviceId
   * @param {string|Date} params.referenceDate
   * @returns {Promise<{referenceDate: string, prices: any[]}>}
   */
  async getPriceList ({ skus, locationFsid, serviceId, referenceDate }, pgClient) {
    const normalizedSkus = Array.isArray(skus) && skus.length > 0 ? skus.map(sku => sku.replace('product:', '')) : null
    const normalizedReferenceDate = referenceDate || null
    const queryParams = [normalizedSkus, normalizedReferenceDate, locationFsid || null, serviceId || null]

    const query = `
      with product_base as (
        select
          p.old_sku_id as sku,
          m.product_market_code,
          m.aliases,
          m.id as market_id,
          p.vat
        from avocado.data_productsku p
        left join avocado.data_market m on p.market_id = m.id
        where ($1::text[] is null or p.old_sku_id = any($1::text[]))
      ),
      prices as (
        select
          sell.sell_price_id as "sellpriceId",
          buy.buy_price_id as "buypriceId",
          concat('product:', pb.sku) as sku,
          pb.vat,
          avocado.round_half_even(sell.sellprice, 2)::float as sellprice,
          avocado.round_half_even(buy.buyprice, 2)::float as buyprice,
          avocado.round_half_even(sell.discount, 2) as discount,
          avocado.round_half_even(markup.markup * markup.financing_multiplier * (markup.sellprice / (1 + markup.markup + markup.financing_multiplier * markup.markup + markup.delivery_multiplier * markup.markup)), 2) as "financingMarkup",
          avocado.round_half_even(markup.markup * markup.delivery_multiplier * (markup.sellprice / (1 + markup.markup + markup.financing_multiplier * markup.markup + markup.delivery_multiplier * markup.markup)), 2) as "deliveryMarkup"
        from product_base pb
        left join lateral (
          select
            ph.id as sell_price_id,
            ph.sellprice,
            ph.discount,
            ph.date
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          where ph.sku = pb.sku
            and ph.sellprice is not null
            and ph.date <= coalesce($2, now())
            and (
              ($3::text is null and $4::text is null and ph.service_id is null and ph.location_id is null) or
              ($3::text is not null and $4::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $3) or
              ($3::text is null and $4::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $4) or
              ($3::text is not null and $4::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $3 and s.fsid = $4)
            )
          order by ph.date desc
          limit 1
        ) sell on true
        left join lateral (
          select
            ph.id as buy_price_id,
            ph.buyprice,
            ph.date
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          where ph.sku = pb.sku
            and ph.buyprice is not null
            and ph.date <= coalesce($2, now())
            and (
              ($3::text is null and $4::text is null and ph.service_id is null and ph.location_id is null) or
              ($3::text is not null and $4::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $3) or
              ($3::text is null and $4::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $4) or
              ($3::text is not null and $4::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $3 and s.fsid = $4)
            )
          order by ph.date desc
          limit 1
        ) buy on true
        left join lateral (
          select
            ph.id as markup_id,
            ph.sku,
            ph.sellprice,
            ph.markup,
            ph.date,
            mp.financing_multiplier,
            mp.delivery_multiplier
          from real.price_history ph
          left join avocado.data_service s on s.id = ph.service_id
          left join avocado.data_location l on l.uuid = ph.location_id
          left join avocado.data_markupmultiplierhistory mp
            on ph.date::date = mp.date::date
           and (mp.market_id = any(pb.aliases) or mp.market_id = pb.market_id)
          where ph.sku = pb.sku
            and coalesce(ph.markup, 0) > 0
            and ph.date <= coalesce($2, now())
            and mp.id is not null
            and (
              ($3::text is null and $4::text is null and ph.service_id is null and ph.location_id is null) or
              ($3::text is not null and $4::text is null and ph.location_id = l.uuid and ph.service_id is null and l.fsid = $3) or
              ($3::text is null and $4::text is not null and ph.location_id is null and ph.service_id = s.id and s.fsid = $4) or
              ($3::text is not null and $4::text is not null and ph.location_id = l.uuid and ph.service_id = s.id and l.fsid = $3 and s.fsid = $4)
            )
          order by ph.date desc
          limit 1
        ) markup on true
      )
      select
        coalesce((select jsonb_agg(p) from (select * from prices) p), '[]'::jsonb) as prices,
        coalesce($2, now()) as "referenceDate"
    `
    const client = pgClient || this.pgConnection
    const { rows } = await client.query(query, queryParams)
    const response = rows[0]
    response.referenceDate = new Date(response.referenceDate).toISOString()
    return response
  }
}

module.exports = {PricePGAdapter}
